import { WebApp } from '../utils/webAppShim';

const INIT_TIMEOUT_MS = 8000;
const INIT_RETRIES = 8;
const RETRY_DELAY_MS = 400;
const VIEWPORT_STABLE_TIMEOUT_MS = 2200;
/** At most one retry after first failure (each attempt can trigger a full biometric scan). */
const TOKEN_UPDATE_RETRY_DELAYS_MS = [320];

function isViewportLikelyStable() {
    try {
        const h = Number(WebApp.viewportHeight);
        const sh = Number(WebApp.viewportStableHeight);
        return Number.isFinite(h) && Number.isFinite(sh) && sh > 0 && h > 0 && Math.abs(h - sh) < 1;
    } catch {
        return false;
    }
}

/**
 * Wait until Telegram reports a stable viewport or timeout. Reduces Android failures
 * when calling BiometricManager during layout transitions.
 * @param {number} [timeoutMs]
 * @returns {Promise<void>}
 */
export function waitForViewportStable(timeoutMs = VIEWPORT_STABLE_TIMEOUT_MS) {
    return new Promise((resolve) => {
        if (isViewportLikelyStable()) {
            resolve();
            return;
        }
        if (typeof WebApp.onEvent !== 'function' || typeof WebApp.offEvent !== 'function') {
            resolve();
            return;
        }

        let settled = false;
        /** @type {((params: { isStateStable?: boolean }) => void) | null} */
        let handler = null;

        const cleanup = () => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            if (handler) {
                try {
                    WebApp.offEvent('viewportChanged', handler);
                } catch {
                    /* ignore */
                }
            }
            resolve();
        };

        handler = (params) => {
            try {
                const stable = params && (params.isStateStable === true || params.is_state_stable === true);
                if (stable) {
                    cleanup();
                }
            } catch {
                /* ignore */
            }
        };

        const timer = setTimeout(cleanup, timeoutMs);
        try {
            WebApp.onEvent('viewportChanged', handler);
        } catch {
            clearTimeout(timer);
            resolve();
        }
    });
}

function getBiometricManager() {
    return WebApp.BiometricManager || window?.Telegram?.WebApp?.BiometricManager;
}

function getNativeBiometricBridge() {
    try {
        if (typeof window !== 'undefined' && window.BtpScudNativeBiometric) {
            return window.BtpScudNativeBiometric;
        }
    } catch {
        /* ignore */
    }
    return null;
}

export function initBiometric() {
    if (getNativeBiometricBridge()) return Promise.resolve(true);
    return new Promise((resolve) => {
        const bm = getBiometricManager();
        if (!bm) return resolve(false);
        if (bm.isInited) return resolve(true);

        const timer = setTimeout(() => {
            resolve(!!bm.isInited);
        }, INIT_TIMEOUT_MS);
        try {
            try { WebApp.ready(); } catch { /* ignore */ }
            bm.init(() => {
                clearTimeout(timer);
                resolve(true);
            });
        } catch {
            clearTimeout(timer);
            resolve(false);
        }
    });
}

export function getTgPlatform() {
    try {
        return String(WebApp.platform || window?.Telegram?.WebApp?.platform || '').toLowerCase();
    } catch {
        return '';
    }
}

function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function getBiometricInfo() {
    const native = getNativeBiometricBridge();
    if (native && typeof native.getBiometricInfo === 'function') {
        return native.getBiometricInfo();
    }
    for (let attempt = 0; attempt < INIT_RETRIES; attempt += 1) {
        const inited = await initBiometric();
        if (inited) {
            const info = checkAvailability();
            if (info.available) return info;
        }
        await sleep(RETRY_DELAY_MS);
    }
    return checkAvailability();
}

export function checkAvailability() {
    const native = getNativeBiometricBridge();
    if (native && native._syncInfo) {
        return native._syncInfo;
    }
    const bm = getBiometricManager();
    if (!bm || !bm.isInited || !bm.isBiometricAvailable) {
        return { available: false, type: null };
    }
    return { available: true, type: bm.biometricType };
}

export async function requestAccess(reason = 'Для быстрой разблокировки приложения') {
    const native = getNativeBiometricBridge();
    if (native && typeof native.requestAccess === 'function') {
        return native.requestAccess(reason);
    }
    const bm = getBiometricManager();
    if (!bm) return false;
    
    if (!bm.isInited) {
        const inited = await initBiometric();
        if (!inited) return false;
    }
    
    if (bm.isAccessGranted) return true;

    await waitForViewportStable();
    return new Promise((resolve) => {
        try {
            bm.requestAccess({ reason }, (granted) => resolve(!!granted));
        } catch {
            resolve(false);
        }
    });
}

export async function authenticate(reason = 'Подтвердите личность') {
    const native = getNativeBiometricBridge();
    if (native && typeof native.authenticate === 'function') {
        return native.authenticate(reason);
    }
    const bm = getBiometricManager();
    if (!bm) return { success: false, token: null };
    
    if (!bm.isInited) {
        const inited = await initBiometric();
        if (!inited) return { success: false, token: null };
    }
    
    if (!bm.isBiometricAvailable) {
        return { success: false, token: null };
    }
    
    if (!bm.isAccessGranted) {
        const granted = await requestAccess(reason);
        if (!granted) return { success: false, token: null };
    }

    await waitForViewportStable();
    return new Promise((resolve) => {
        try {
            bm.authenticate({ reason }, (success, token) => {
                resolve({ success: !!success, token: token || null });
            });
        } catch {
            resolve({ success: false, token: null });
        }
    });
}

export async function saveToken(token) {
    const bm = getBiometricManager();
    if (!bm) return false;

    if (!bm.isInited) {
        const inited = await initBiometric();
        if (!inited) return false;
    }

    await waitForViewportStable();
    const tryOnce = () => new Promise((resolve) => {
        try {
            bm.updateBiometricToken(token || '', (updated) => resolve(!!updated));
        } catch {
            resolve(false);
        }
    });
    let ok = await tryOnce();
    for (let i = 0; i < TOKEN_UPDATE_RETRY_DELAYS_MS.length && !ok; i += 1) {
        await sleep(TOKEN_UPDATE_RETRY_DELAYS_MS[i]);
        await waitForViewportStable(Math.min(VIEWPORT_STABLE_TIMEOUT_MS, 900));
        ok = await tryOnce();
    }
    return ok;
}

let tokenUpdateInFlight = false;

export function generateBiometricToken(length = 32) {
    // One decimal digit per byte (bytes[i] % 10), so we need at least `length` bytes for `length` digits.
    const byteCount = Math.max(16, length);
    const bytes = new Uint8Array(byteCount);
    try {
        const cryptoObj = (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues)
            ? window.crypto
            : null;
        if (cryptoObj) cryptoObj.getRandomValues(bytes);
        else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
    } catch {
        for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
    }
    let out = '';
    for (let i = 0; i < byteCount && out.length < length; i += 1) out += String(bytes[i] % 10);
    return out.slice(0, length);
}

export async function createBiometricProof(token, challenge) {
    const normalizedToken = String(token || '').trim();
    const normalizedChallenge = String(challenge || '').trim();
    if (!normalizedToken || !normalizedChallenge) return null;
    const cryptoObj = (typeof window !== 'undefined' && window.crypto) ? window.crypto : null;
    const TextEncoderCtor = (typeof window !== 'undefined' && window.TextEncoder) ? window.TextEncoder : null;
    if (!cryptoObj || !cryptoObj.subtle || !TextEncoderCtor) return null;
    try {
        const encoded = new TextEncoderCtor().encode(`${normalizedChallenge}:${normalizedToken}`);
        const digest = await cryptoObj.subtle.digest('SHA-256', encoded);
        const bytes = Array.from(new Uint8Array(digest));
        return bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch {
        return null;
    }
}

export function saveTokenInteractive(token, reason = 'Для использования биометрии') {
    const native = getNativeBiometricBridge();
    if (native && typeof native.saveTokenInteractive === 'function') {
        return native.saveTokenInteractive(token, reason);
    }
    const bm = getBiometricManager();
    if (!bm) return Promise.resolve(false);
    if (tokenUpdateInFlight) return Promise.resolve(false);
    tokenUpdateInFlight = true;

    const finish = (result) => {
        tokenUpdateInFlight = false;
        return result;
    };

    const doSingleUpdate = () => new Promise((resolve) => {
        try {
            bm.updateBiometricToken(token || '', (updated) => resolve(!!updated));
        } catch {
            resolve(false);
        }
    });

    const doUpdate = async () => {
        await waitForViewportStable();
        let ok = await doSingleUpdate();
        for (let i = 0; i < TOKEN_UPDATE_RETRY_DELAYS_MS.length && !ok; i += 1) {
            await sleep(TOKEN_UPDATE_RETRY_DELAYS_MS[i]);
            await waitForViewportStable(Math.min(VIEWPORT_STABLE_TIMEOUT_MS, 900));
            ok = await doSingleUpdate();
        }
        return ok;
    };

    const afterInit = async () => {
        if (!bm.isBiometricAvailable) return false;
        if (bm.isAccessGranted) {
            await waitForViewportStable();
            return doUpdate();
        }
        const granted = await requestAccess(reason);
        if (!granted) return false;
        await waitForViewportStable();
        return doUpdate();
    };

    return new Promise((resolve) => {
        try { WebApp.ready(); } catch { /* ignore */ }
        try {
            if (bm.isInited) {
                afterInit().then((r) => resolve(finish(r)));
                return;
            }
            bm.init(() => {
                afterInit().then((r) => resolve(finish(r)));
            });
        } catch {
            resolve(finish(false));
        }
    });
}

export function removeToken() {
    return saveToken('');
}

export function isBiometricTokenSaved() {
    const bm = getBiometricManager();
    return !!(bm && bm.isBiometricTokenSaved);
}

export function isAccessGranted() {
    const bm = getBiometricManager();
    return !!(bm && bm.isAccessGranted);
}

export function openBiometricSettings() {
    const bm = getBiometricManager();
    if (bm && typeof bm.openSettings === 'function') {
        try {
            bm.openSettings();
            return true;
        } catch {
            return false;
        }
    }
    return false;
}

export function normalizeBiometricType(type) {
    const normalized = String(type || '').toLowerCase();
    if (normalized.includes('face')) return 'face';
    if (normalized.includes('finger') || normalized.includes('touch')) return 'touch';
    return 'unknown';
}

export function normalizeBiometricMode(mode, fallback = 'any') {
    const normalized = normalizeBiometricType(mode);
    if (normalized === 'face' || normalized === 'touch') return normalized;
    return fallback;
}

/** True if device-reported biometrics match account `biometricMode` (any | face | touch). */
export function isBiometricTypeAllowedForAccountMode(accountMode, deviceBiometricTypeRaw) {
    const selectedMode = normalizeBiometricMode(accountMode, 'any');
    const currentMode = normalizeBiometricType(deviceBiometricTypeRaw);
    return currentMode === 'unknown' || selectedMode === 'any' || selectedMode === currentMode;
}

/**
 * @param {string|null|undefined} type - Telegram `biometricType` or normalized hint
 * @param {string|null|undefined} [platformOverride] - e.g. from WebApp.platform; defaults to current Mini App platform
 */
export function getBiometricLabel(type, platformOverride) {
    const biometricType = normalizeBiometricType(type);
    const platform = platformOverride != null && String(platformOverride).trim()
        ? String(platformOverride).toLowerCase()
        : getTgPlatform();
    if (biometricType === 'face') return 'Face ID';
    if (biometricType === 'touch') {
        if (platform === 'android') return 'Отпечаток пальца';
        return 'Touch ID';
    }
    return 'Биометрия';
}

/**
 * Subscribe to Telegram BiometricManager state changes (e.g. after user grants access in system UI).
 * @param {() => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeBiometricManagerUpdated(callback) {
    if (typeof callback !== 'function') return () => {};
    const handler = () => {
        try {
            callback();
        } catch {
            /* ignore subscriber errors */
        }
    };
    try {
        if (typeof WebApp.onEvent === 'function') {
            WebApp.onEvent('biometricManagerUpdated', handler);
            return () => {
                try {
                    if (typeof WebApp.offEvent === 'function') {
                        WebApp.offEvent('biometricManagerUpdated', handler);
                    }
                } catch {
                    /* ignore */
                }
            };
        }
    } catch {
        /* ignore */
    }
    return () => {};
}
